import { useId, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Bot,
  CircleCheck,
  Headset,
  Loader2,
  TriangleAlert,
  X,
} from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { PermissionButton } from "@/components/Auth"
import { Permission, type Workspace } from "@/types"
import type {
  ReplyModeAgent,
  ReplyModeBatch,
  ReplyModeBatchItemError,
  ReplyModeTarget,
} from "@/hooks/api"
import type { ReplyModeBatchController } from "./useReplyModeBatch"

const KEY = "crm.userManagement.replyMode"
const REPLY_MODES: ReplyModeTarget[] = ["human", "ai"]
const MODE_ICONS = { human: Headset, ai: Bot } as const

const ITEM_ERROR_KEYS: Record<ReplyModeBatchItemError, string> = {
  client_unavailable: `${KEY}.results.errors.clientUnavailable`,
  agent_unavailable: `${KEY}.results.errors.agentUnavailable`,
  unpublished: `${KEY}.results.errors.unpublished`,
  human_only: `${KEY}.results.errors.humanOnly`,
  conflict: `${KEY}.results.errors.conflict`,
}

const getReplyModeItemErrorKey = (error: string | null) =>
  (error && ITEM_ERROR_KEYS[error as ReplyModeBatchItemError]) ||
  `${KEY}.results.errors.generic`

/** Why each target mode is unavailable for the agent, if at all. */
const getReplyModeDisabledReason = (
  agent: Pick<ReplyModeAgent, "is_published" | "agent_response_mode"> | null,
  mode: ReplyModeTarget
): "noAgent" | "unpublished" | "humanOnly" | null => {
  if (!agent) return "noAgent"
  if (!agent.is_published) return "unpublished"
  if (mode === "ai" && agent.agent_response_mode === "human_only") {
    return "humanOnly"
  }
  return null
}

const getAgentBadgeKey = (agent: ReplyModeAgent) => {
  if (!agent.is_published) return `${KEY}.agentUnpublishedBadge`
  if (agent.agent_response_mode === "human_only")
    return `${KEY}.agentHumanOnlyBadge`
  return null
}

export interface ReplyModeAgentSelectProps {
  agents: ReplyModeAgent[]
  value: string
  onChange: (agentId: string) => void
  disabled?: boolean
  permissionDenied?: boolean
  loadFailed?: boolean
}

export const ReplyModeAgentSelect = ({
  agents,
  value,
  onChange,
  disabled,
  permissionDenied,
  loadFailed,
}: ReplyModeAgentSelectProps) => {
  const { t } = useTranslation()
  const id = useId()
  const helperId = `${id}-helper`

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {t(`${KEY}.agentLabel`)}
      </Label>
      <Select
        value={value || undefined}
        onValueChange={onChange}
        disabled={disabled || agents.length === 0}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={permissionDenied ? 0 : undefined} className="w-fit">
              <SelectTrigger
                id={id}
                className="w-64"
                aria-label={t(`${KEY}.agentLabel`)}
                aria-describedby={helperId}
              >
                <SelectValue
                  placeholder={t(
                    agents.length === 0
                      ? `${KEY}.noAgents`
                      : `${KEY}.agentPlaceholder`
                  )}
                />
              </SelectTrigger>
            </span>
          </TooltipTrigger>
          {permissionDenied && (
            <TooltipContent>{t("common.noPermission")}</TooltipContent>
          )}
        </Tooltip>
        <SelectContent>
          {agents.map((agent) => {
            const badgeKey = getAgentBadgeKey(agent)
            return (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.name || t(`${KEY}.unnamedAgent`)}
                {badgeKey && (
                  <span className="shrink-0 rounded-full border px-1.5 text-[11px] leading-4 whitespace-nowrap text-muted-foreground">
                    {t(badgeKey)}
                  </span>
                )}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      <p
        id={helperId}
        className={cn(
          "max-w-64 text-xs",
          loadFailed ? "text-destructive" : "text-muted-foreground"
        )}
      >
        {loadFailed ? t(`${KEY}.loadAgentsError`) : t(`${KEY}.agentHelper`)}
      </p>
    </div>
  )
}

export interface ReplyModeSelectionActionsProps {
  workspace: Workspace
  readOnly?: boolean
  agent: ReplyModeAgent | null
  controller: ReplyModeBatchController
  /** Selected customer count shown before a snapshot exists. */
  selectionCount: number
  /** Returns the snapshot to confirm (existing or newly created). */
  onPrepare: () => Promise<ReplyModeBatch | null>
  onRecheck: () => Promise<ReplyModeBatch | null>
  onConfirm: (mode: ReplyModeTarget) => void
}

export const ReplyModeSelectionActions = ({
  workspace,
  readOnly,
  agent,
  controller,
  selectionCount,
  onPrepare,
  onRecheck,
  onConfirm,
}: ReplyModeSelectionActionsProps) => {
  const { t } = useTranslation()
  const reasonId = useId()
  const [confirmMode, setConfirmMode] = useState<ReplyModeTarget | null>(null)
  const [intentMode, setIntentMode] = useState<ReplyModeTarget | null>(null)
  const { batch, busy, canCreate, phase } = controller
  const agentName = agent?.name || t(`${KEY}.unnamedAgent`)
  // Saved results belong to a previous command, not this fresh selection.
  const selectionBatch = controller.requestedMode ? null : batch
  // Prefer the current selection's server snapshot once available.
  const snapshotAgent =
    agent && selectionBatch?.agent_id === agent.id ? selectionBatch : null
  const confirmedAgentName = snapshotAgent?.agent_name || agentName
  const frozenMode = selectionBatch?.interaction_mode ?? null

  const reasons = REPLY_MODES.map((mode) => {
    const reason = getReplyModeDisabledReason(snapshotAgent ?? agent, mode)
    if (reason) return reason
    if (frozenMode && frozenMode !== mode) return "frozen"
    return null
  })
  const visibleReasons = readOnly
    ? []
    : Array.from(new Set(reasons.filter(Boolean)))

  const handleModeClick = async (mode: ReplyModeTarget) => {
    if (!canCreate) return
    setIntentMode(mode)
    const prepared = await onPrepare()
    if (prepared) setConfirmMode(mode)
  }

  const handleRecheck = async () => {
    const recovered = await onRecheck()
    if (recovered && intentMode) setConfirmMode(intentMode)
  }

  const confirmCount = selectionBatch?.total ?? selectionCount
  const confirmRows = confirmMode
    ? [
        [`${KEY}.confirm.agent`, confirmedAgentName],
        [
          `${KEY}.confirm.customers`,
          t(`${KEY}.confirm.customerCount`, { count: confirmCount }),
        ],
        [`${KEY}.confirm.mode`, t(`${KEY}.modeShort.${confirmMode}`)],
        ...(batch?.snapshot_at
          ? [
              [
                `${KEY}.confirm.snapshot`,
                new Date(batch.snapshot_at).toLocaleString(),
              ],
            ]
          : []),
      ]
    : []

  return (
    <div className="flex flex-col gap-2">
      <div
        role="group"
        aria-label={t(`${KEY}.actionsLabel`, { agent: agentName })}
        aria-describedby={visibleReasons.length ? reasonId : undefined}
        className="flex flex-wrap items-center gap-2"
      >
        <span className="text-xs font-medium text-muted-foreground">
          {t(`${KEY}.selection.replyModeGroup`)}
        </span>
        {REPLY_MODES.map((mode, index) => {
          const Icon = MODE_ICONS[mode]
          return (
            <PermissionButton
              key={mode}
              permission={Permission.WsUpdateResources}
              workspace={workspace}
              disabledTooltip={t("common.noPermission")}
              type="button"
              variant="outline"
              size="sm"
              disabled={readOnly || !canCreate || !!reasons[index]}
              onClick={() => void handleModeClick(mode)}
            >
              <Icon aria-hidden />
              {t(`${KEY}.modes.${mode}`)}
            </PermissionButton>
          )
        })}
      </div>
      {visibleReasons.length > 0 && (
        <div id={reasonId} className="space-y-1">
          {visibleReasons.map((reason) => (
            <p key={reason} className="text-xs text-muted-foreground">
              {t(`${KEY}.disabled.${reason}`, {
                agent: confirmedAgentName,
                mode: frozenMode ? t(`${KEY}.modeShort.${frozenMode}`) : "",
              })}
            </p>
          ))}
        </div>
      )}
      {phase === "createUnknown" && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span className="flex-1">{t(`${KEY}.selection.createUnknown`)}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void handleRecheck()}
          >
            {t(`${KEY}.selection.checkAgain`)}
          </Button>
        </div>
      )}

      <AlertDialog
        open={confirmMode !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmMode(null)
        }}
      >
        <AlertDialogContent>
          {confirmMode && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(`${KEY}.confirm.title.${confirmMode}`, {
                    count: confirmCount,
                  })}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(`${KEY}.confirm.impact.${confirmMode}`, {
                    agent: confirmedAgentName,
                  })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 rounded-md border bg-muted/40 px-4 py-3 text-sm">
                {confirmRows.map(([labelKey, value]) => (
                  <div key={labelKey} className="contents">
                    <dt className="text-muted-foreground">{t(labelKey)}</dt>
                    <dd className="font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-xs text-muted-foreground">
                {t(`${KEY}.confirm.inFlightNote`)}
              </p>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {t(`${KEY}.confirm.cancel`)}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={() => {
                    const mode = confirmMode
                    setConfirmMode(null)
                    onConfirm(mode)
                  }}
                >
                  {t(`${KEY}.confirm.submit.${confirmMode}`)}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export interface ReplyModeBatchResultsProps {
  controller: ReplyModeBatchController
  getCustomerLabel: (clientUserId: string, index: number) => string
  onDismiss: () => void
}

export const ReplyModeBatchResults = ({
  controller,
  getCustomerLabel,
  onDismiss,
}: ReplyModeBatchResultsProps) => {
  const { t } = useTranslation()
  const titleId = useId()
  const { batch, phase, busy, interrupted, requestedMode } = controller
  const mode = batch?.interaction_mode ?? requestedMode
  if (!batch || !mode) return null

  const agentName = batch.agent_name || t(`${KEY}.unnamedAgent`)
  const processed = batch.total - batch.pending
  const percent = batch.total ? Math.round((processed / batch.total) * 100) : 0
  const isUnknown = phase === "unknown"
  const canAct = !busy && !isUnknown
  const needsAttention = isUnknown || interrupted || batch.failed > 0

  const statusText =
    phase === "executing"
      ? t(`${KEY}.results.progress`, { done: processed, total: batch.total })
      : phase === "reconciling"
        ? t(`${KEY}.results.reconciling`)
        : isUnknown
          ? t(`${KEY}.results.unknown`)
          : interrupted
            ? t(`${KEY}.results.interrupted`)
            : t(`${KEY}.results.done`)

  const StatusIcon = busy
    ? Loader2
    : needsAttention
      ? TriangleAlert
      : CircleCheck

  const counts = [
    { key: "changed", value: batch.changed, tone: "" },
    { key: "already", value: batch.already, tone: "" },
    { key: "failed", value: batch.failed, tone: "text-destructive" },
    {
      key: "pending",
      value: batch.pending,
      tone: "text-amber-600 dark:text-amber-400",
    },
  ] as const

  return (
    <section
      aria-labelledby={titleId}
      className="space-y-4 rounded-lg border bg-card p-4"
    >
      <div className="flex items-start gap-3">
        <StatusIcon
          aria-hidden
          className={cn(
            "mt-0.5 size-4 shrink-0",
            busy && "animate-spin text-muted-foreground",
            !busy && needsAttention && "text-amber-600 dark:text-amber-400",
            !busy && !needsAttention && "text-emerald-600 dark:text-emerald-400"
          )}
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <h4 id={titleId} className="text-sm font-semibold">
            {t(`${KEY}.results.title`, {
              mode: t(`${KEY}.modeShort.${mode}`),
              agent: agentName,
            })}
          </h4>
          <p
            role="status"
            aria-live="polite"
            className={cn(
              "text-sm",
              isUnknown
                ? "text-amber-700 dark:text-amber-400"
                : "text-muted-foreground"
            )}
          >
            {statusText}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="-mt-1.5 -mr-1.5"
          disabled={busy}
          onClick={onDismiss}
          aria-label={t(`${KEY}.results.dismiss`)}
          title={t(`${KEY}.results.dismiss`)}
        >
          <X aria-hidden />
        </Button>
      </div>

      <div
        role="progressbar"
        aria-labelledby={titleId}
        aria-valuemin={0}
        aria-valuemax={batch.total}
        aria-valuenow={processed}
        className="h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-4">
        {counts.map(({ key, value, tone }) => (
          <div key={key} className="space-y-0.5 bg-card px-3 py-2">
            <dt className="text-xs text-muted-foreground">
              {t(`${KEY}.results.counts.${key}`)}
            </dt>
            <dd
              className={cn(
                "text-lg font-semibold tabular-nums",
                value > 0 && tone
              )}
              data-testid={`reply-mode-count-${key}`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>

      {batch.items.length > 0 && (
        <div className="overflow-hidden rounded-md border">
          <h5 className="border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
            {t(`${KEY}.results.failedListTitle`)}
          </h5>
          <ul className="max-h-48 divide-y overflow-y-auto text-sm">
            {batch.items.map((item, index) => (
              <li
                key={item.client_user_id}
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-3 py-2"
              >
                <span className="font-medium">
                  {getCustomerLabel(item.client_user_id, index)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t(getReplyModeItemErrorKey(item.error))}
                </span>
              </li>
            ))}
          </ul>
          {batch.failed > 50 && (
            <div className="flex justify-end gap-2 border-t px-3 py-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canAct}
                onClick={() => void controller.loadFailedPage()}
              >
                {t(`${KEY}.results.firstFailurePage`)}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canAct || !batch.next_cursor}
                onClick={() =>
                  void controller.loadFailedPage(batch.next_cursor ?? undefined)
                }
              >
                {t(`${KEY}.results.nextFailurePage`)}
              </Button>
            </div>
          )}
        </div>
      )}

      {(isUnknown || (canAct && (batch.pending > 0 || batch.failed > 0))) && (
        <div className="flex flex-wrap items-center gap-2">
          {isUnknown && (
            <Button
              type="button"
              size="sm"
              onClick={() => void controller.recheck()}
            >
              {t(`${KEY}.results.checkSaved`)}
            </Button>
          )}
          {canAct && batch.pending > 0 && (
            <Button
              type="button"
              size="sm"
              onClick={() => void controller.continuePending()}
            >
              {t(`${KEY}.results.continue`, { count: batch.pending })}
            </Button>
          )}
          {canAct && batch.failed > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void controller.retryFailed()}
            >
              {t(`${KEY}.results.retryFailed`, { count: batch.failed })}
            </Button>
          )}
        </div>
      )}
    </section>
  )
}
